import { Phone, Trash2, Truck, UserPlus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
    addTransporter,
    fetchTransporters,
    removeTransporter,
    type Transporter,
} from './dispatchApi';

export const TransportersDialog = ({
    open,
    onOpenChange,
    onChanged,
}: {
    open: boolean;
    onOpenChange: (o: boolean) => void;
    onChanged?: () => void;
}) => {
    const [rows, setRows] = useState<Transporter[]>([]);
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            setRows(await fetchTransporters());
        } catch (e: any) {
            toast.error(e.message);
        }
    }, []);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    const add = async () => {
        if (!name.trim()) return;
        setBusy(true);
        try {
            await addTransporter(name, phone);
            setName('');
            setPhone('');
            await load();
            onChanged?.();
        } catch (e: any) {
            toast.error(e.message);
        } finally {
            setBusy(false);
        }
    };

    const remove = async (t: Transporter) => {
        if (!window.confirm(`Delete transporter "${t.name}"?`)) return;
        try {
            await removeTransporter(t.id);
            await load();
            onChanged?.();
        } catch (e: any) {
            toast.error(e.message);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg rounded-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-primary">
                        <Truck className="size-5" /> Transporters
                    </DialogTitle>
                    <DialogDescription>
                        Manage transporters and their WhatsApp numbers for
                        enquiries.
                    </DialogDescription>
                </DialogHeader>

                <div className="rounded-xl border bg-primary/5 p-3 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                        <Input
                            placeholder="Transporter name"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            id="transporter-name"
                        />
                        <Input
                            placeholder="WhatsApp number"
                            value={phone}
                            onChange={e => setPhone(e.target.value)}
                            id="transporter-phone"
                            onKeyDown={e => e.key === 'Enter' && add()}
                        />
                    </div>
                    <Button
                        id="add-transporter-btn"
                        className="w-full"
                        onClick={add}
                        disabled={busy || !name.trim()}
                    >
                        <UserPlus /> Add Transporter
                    </Button>
                </div>

                <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1">
                    {rows.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-6">
                            No transporters yet.
                        </p>
                    )}
                    {rows.map(t => (
                        <div
                            key={t.id}
                            className="flex items-center justify-between rounded-lg border bg-card px-3 py-2 hover:border-primary/50 transition-colors"
                        >
                            <div className="min-w-0">
                                <div className="font-medium truncate">
                                    {t.name}
                                </div>
                                <div className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Phone className="size-3" />
                                    {t.phone || 'no phone'}
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                className="text-destructive hover:bg-destructive/10"
                                onClick={() => remove(t)}
                                aria-label={`Delete ${t.name}`}
                            >
                                <Trash2 />
                            </Button>
                        </div>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
};
